import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Ad site interface
export interface AdSite {
  url: string;
  displayName: string;
  title: string;
}

interface SponsoredLinksContextType {
  getAdSiteByIndex: (index: number) => AdSite | null;
  totalAdSites: number;
  isLoading: boolean;
  reloadSponsors: () => Promise<void>;
}

const SponsoredLinksContext = createContext<SponsoredLinksContextType | undefined>(undefined);

export function SponsoredLinksProvider({ children }: { children: ReactNode }) {
  const [adSites, setAdSites] = useState<AdSite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Function to load ad sites from the JSON file
  const loadAdSites = async () => {
    setIsLoading(true);
    try {
      // Add cache-busting query parameter to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await fetch(`/data/sponsored-links.json?t=${timestamp}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.sponsors && Array.isArray(data.sponsors)) {
          setAdSites(data.sponsors);
        }
      }
    } catch (error) {
      console.error('Error loading sponsored links:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load ad sites on mount
  useEffect(() => {
    loadAdSites();
  }, []);

  // Get an ad site by its index
  const getAdSiteByIndex = (index: number): AdSite | null => {
    if (index < 0 || index >= adSites.length) {
      return null;
    }
    return adSites[index];
  };

  const value = {
    getAdSiteByIndex,
    totalAdSites: adSites.length,
    isLoading,
    reloadSponsors: loadAdSites
  };

  return (
    <SponsoredLinksContext.Provider value={value}>
      {children}
    </SponsoredLinksContext.Provider>
  );
}

export function useSponsoredLinks() {
  const context = useContext(SponsoredLinksContext);
  if (context === undefined) {
    throw new Error('useSponsoredLinks must be used within a SponsoredLinksProvider');
  }
  return context;
}
